import { useEffect, useRef, useState } from 'react';
import { fetchAndCacheMedia, getMediaBlob } from '../utils/mediaSessionCache';

/**
 * Stabilizes a media object URL so that URL.createObjectURL() is only called
 * when the blob reference genuinely changes — not on every render.
 *
 * Prefers an in-memory blob (recording or session cache). Otherwise fetches
 * `fileKey` via the authenticated download endpoint, caches the blob for the
 * session, and creates a local object URL. Direct API URLs cannot be used as
 * <audio>/<video> src (cross-origin + Authorization).
 *
 * @param {Object} params
 * @param {Blob|null} [params.blob] - In-memory blob (from recording or cache). Takes priority.
 * @param {string|null} [params.fileKey] - Stored media key used for fetch + session cache.
 * @param {string|null} [params.questionId] - Optional alias key for the session cache.
 * @param {string|null} [params.token] - Bearer token used when fetching.
 * @param {string|null} [params.fallbackUrl] - Deprecated; prefer fileKey.
 * @returns {{ mediaUrl: string|null, isLoading: boolean }}
 */
export const useStableMediaUrl = ({ blob, fileKey, questionId, token, fallbackUrl }) => {
  const objectUrlRef = useRef(null);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const revokeObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const setObjectUrl = (nextBlob) => {
      revokeObjectUrl();
      const url = URL.createObjectURL(nextBlob);
      objectUrlRef.current = url;
      setMediaUrl(url);
    };

    const load = async () => {
      const resolvedBlob =
        blob || getMediaBlob({ fileKey, questionId }) || null;

      if (resolvedBlob) {
        if (!cancelled) {
          setIsLoading(false);
          setObjectUrl(resolvedBlob);
        }
        return;
      }

      if (!fileKey || !token) {
        // Legacy path if a caller still passes fallbackUrl only.
        if (fallbackUrl && token) {
          setIsLoading(true);
          try {
            const response = await fetch(fallbackUrl, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) {
              throw new Error(`Failed to fetch media file (${response.status})`);
            }
            const fetchedBlob = await response.blob();
            if (cancelled) return;
            setObjectUrl(fetchedBlob);
          } catch (error) {
            console.error('Error fetching media file:', error);
            if (!cancelled) {
              revokeObjectUrl();
              setMediaUrl(null);
            }
          } finally {
            if (!cancelled) setIsLoading(false);
          }
          return;
        }

        revokeObjectUrl();
        setMediaUrl(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const fetchedBlob = await fetchAndCacheMedia({ fileKey, token, questionId });
        if (cancelled || !fetchedBlob) return;
        setObjectUrl(fetchedBlob);
      } catch (error) {
        console.error('Error fetching media file:', error);
        if (!cancelled) {
          revokeObjectUrl();
          setMediaUrl(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      revokeObjectUrl();
    };
  }, [blob, fileKey, questionId, token, fallbackUrl]);

  return { mediaUrl, isLoading };
};
